import * as ClassNames from 'classnames'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import * as constants from '../../static/constants'
import { Button, Spinner } from 'components/atoms'
import Pagination from '../atoms/Pagination'

export interface IColumn {
  name: string
  field: string
  fullWidth?: boolean
  required?: boolean
  readOnly?: boolean
  className?: string
  link?(record: any): string
  renderElement?(record: any): React.ReactElement
  renderHeader?(): React.ReactElement
}

interface IProps {
  columns: IColumn[]
  records: any[]
  editable: boolean
  sortable?: boolean
  sortableColumns?: string[]
  pagination?: any
  loading?: boolean
  isCustomResource?: boolean
  stickyActions?: boolean
  actionHeader?: string
  getRowLink?(record: any): string
  handleDelete?(id: number): void
  getEditLink?(record: any): string
  renderRecordControls?(record: any): React.ReactElement
  getRowClassName?(record: any): string
  getResourceByPage?(page: number): void
}

const EditTable: React.FC<IProps> = props => {
  const sizerRef = React.useRef(null)
  const tableRef = React.useRef(null)
  const onChangePageHandler = React.useCallback(
    page => {
      if (props.isCustomResource === true) {
        props.getResourceByPage(page)
      } else {
        location.href = `${location.pathname}?page=${page}`
      }
    },
    [props.getResourceByPage]
  )

  // Sort Table related
  const sortableColumns = props.sortableColumns || []
  const getCurrentSortColumn = () => {
    const urlParams = new URLSearchParams(window.location.search)

    return urlParams.get('sort') || sortableColumns[0]
  }

  const getCurrentSortDirection = () => {
    const urlParams = new URLSearchParams(window.location.search)

    return urlParams.get('direction') || 'desc'
  }

  const [order, setOrder] = React.useState({
    column: getCurrentSortColumn(),
    direction: getCurrentSortDirection(),
  })

  const getDirection = column => {
    if (order.column === column) {
      return order.direction === 'asc' ? 'desc' : 'asc'
    } else {
      return 'desc'
    }
  }

  const changeOrder = React.useCallback(async column => {
    const direction = getDirection(column)
    const params = new URLSearchParams(window.location.search)
    params.set('sort', column)
    params.set('direction', direction)
    location.href = `${location.pathname}?${params.toString()}`
  }, [])

  const getOrderIndicatorAndColumnName = column => {
    if (props.sortable && sortableColumns.includes(column.field)) {
      return (
        <span className="indicator" onClick={() => changeOrder(column.field)}>
          {order.direction === 'desc' ? '↓' : '↑'}
          {` ${column.name}`}
        </span>
      )
    } else {
      return column.renderHeader ? column.renderHeader() : column.name
    }
  }
  // End of sort Table related

  const getLink = record => {
    if (typeof props.getRowLink === 'function') {
      return props.getRowLink(record)
    }

    return null
  }

  return (
    <EditTableWrapper>
      <div className="Table_Scroller">
        <table ref={tableRef} className="Table">
          <thead className="Table_Head">
            <tr className="Table_Row">
              {props.columns.map(column => (
                <th className={ClassNames('Table_Header', column.className)} key={column.name}>
                  {getOrderIndicatorAndColumnName(column)}
                </th>
              ))}
              {props.editable && (
                <th
                  className={`Table_Header ${props.stickyActions &&
                    props.records?.length !== 0 &&
                    '_sticky'}`}
                >
                  {props.actionHeader || I18n.t('generic.operation')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {props.loading ? (
              <tr>
                <td colSpan={props.columns.length}>
                  <Spinner />
                </td>
              </tr>
            ) : (
              props.records.map((record, index) => (
                <EditTableRow
                  key={`${record.id}-${index}`}
                  editable={props.editable}
                  columns={props.columns}
                  record={record}
                  link={getLink(record)}
                  handleDelete={props.handleDelete}
                  getEditLink={props.getEditLink}
                  renderRecordControls={props.renderRecordControls}
                  getRowClassName={props.getRowClassName}
                  stickyActions={props.stickyActions}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {props.records?.length === 0 && !props.loading && (
        <div className="nodata">{I18n.t('generic.no_data')}</div>
      )}
      {props.pagination && !props.loading && (
        <Pagination
          onChangePageHandler={onChangePageHandler}
          currentPage={props.pagination.current_page}
          prevPage={props.pagination.prev_page}
          nextPage={props.pagination.next_page}
          totalPages={props.pagination.total_pages}
          totalCount={props.pagination.total_count}
        />
      )}
    </EditTableWrapper>
  )
}

interface ICategory {
  id: number
  name_i18n: string
  position: number
}

interface IEditTableRowProps {
  editable: boolean
  columns: IColumn[]
  record: ICategory
  link?: string
  stickyActions: boolean | null
  handleDelete?(id: number): void
  getEditLink(record: any): string
  renderRecordControls?(record: any): React.ReactElement
  getRowClassName?(record: any): string
}

const EditTableRow: React.FC<IEditTableRowProps> = props => {
  const editLink = typeof props.getEditLink === 'function' ? props.getEditLink(props.record) : null
  const showDetail = () => {
    if (props.link) {
      location.href = props.link
    }
  }

  return (
    <tr
      className={`${props.link ? 'Link ' : ''}Table_Row ${
        props.getRowClassName ? props.getRowClassName(props.record) : ''
      }`}
    >
      {props.columns.map(column => (
        <td
          onClick={showDetail}
          key={column.field}
          className={ClassNames(
            'Table_Data',
            column.className,
            {
              hasElement: typeof column.renderElement === 'function',
            },
            {
              fullWidth: column.fullWidth,
            }
          )}
        >
          {typeof column.link === 'function' ? (
            <a href={column.link(props.record)} target="_blank">
              {props.record[column.field]}
            </a>
          ) : typeof column.renderElement === 'function' ? (
            column.renderElement(props.record)
          ) : (
            (typeof props.record[column.field] === 'string' ||
              typeof props.record[column.field] === 'number') &&
            props.record[column.field]
          )}
        </td>
      ))}
      {props.editable && (
        <td className={`Table_Data _buttons ${props.stickyActions && '_sticky'}`}>
          {editLink && (
            <Button key="edit" small={true}>
              <a href={editLink}>{I18n.t('generic.edit')}</a>
            </Button>
          )}
          {props.handleDelete && (
            <Button
              key="delete"
              small={true}
              handleClick={() => props.handleDelete(props.record.id)}
            >
              {I18n.t('generic.delete')}
            </Button>
          )}
          {typeof props.renderRecordControls === 'function' &&
            props.renderRecordControls(props.record)}
        </td>
      )}
    </tr>
  )
}

const EditTableWrapper = styled.div`
  min-width: 0;
  .Pagination {
    padding-bottom: 24px;
  }

  .EditTable_Title {
    font-size: 18px;
    padding: 16px 20px;

    .Button {
      margin-left: 16px;
    }
  }
  .nodata {
    margin: 0 auto 16px;
    padding: 0 24px;
    font-size: 14px;
    font-weight: 700;
  }
  .Table_Scroller {
    width: 100%;
    overflow: auto;
  }
  .Table {
    min-width: 100%;
    .Table_Head {
      border-top: solid 1px ${constants.COLORS.divider};
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        display: none;
      }
    }
    .Table_Header {
      padding: 12px 24px;
      font-size: 12px;
      color: ${constants.COLORS.subText};

      span.indicator {
        cursor: pointer;
      }
    }
    .Table_Data {
      height: 88px;
      padding: 0 8px 0 24px;
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        padding: 8px 0;
        height: auto;
      }
    }
    .Table_Header,
    .Table_Data {
      max-width: 400px;
      min-width: 128px;
      font-size: 12px;
      word-break: break-all;
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        padding: 0;
      }
      &._buttons {
        width: 180px;
        @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
          width: auto;
        }
        button {
          @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
            margin: 4px !important;
          }
        }
      }
      &.fullWidth {
        max-width: unset;
        min-width: unset;
      }
    }
      > a {
        color: ${constants.COLORS.primary};
      }
    }

    .Table_Row {
      border-top: solid 1px ${constants.COLORS.divider};
      .Table_Header,
      .Table_Data {
        &:first-child {
          padding-left: 0;
        }
      }
      &.Link {
        &:hover {
          .Table_Data:not(._buttons) {
            cursor: pointer;
            opacity: 0.8;
          }
        }
      }
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 16px 0;
      }
    }
    ._sticky {
      position:-webkit-sticky;
	    position:sticky;
      right: 0;
      border: none;
    }
    ._sticky:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: white;
      border-left: solid 0.5px ${constants.COLORS.divider};
      z-index: -1;
    }
  }
  
  .Spinner {
    margin-top: 20px;
  }
`

export default EditTable
